/*******************************************************************************
 * Copyright 2026 Collaterate. All rights reserved.
 *******************************************************************************/

// TTI-20177 Plan B: REST-mode dispatch shim. Screens register an adapter per
// (handlerUrl, COMET action); when the session user holds ADMIN_REST_UI (the
// "REST Admin Pilot" role emits `restUiEnabled` from siteAdministrationHead.jsp),
// CometRequest.send() routes the call through the adapter instead of the COMET
// channel. The adapter performs the REST call(s) and settles the SAME request
// object via restComet.succeed / failValidation / fail, so existing success and
// failure callbacks — and everything they read off the request (message arrays,
// getObject/getWorkingObject) — run unchanged. Users without the role never
// enter this file's code path.
var restComet = (function () {
    var adapters = {};

    var STALE_AFTER_MUTATION = 'This change was saved, but the page could not be refreshed. Reload the page to see the current values.';

    function key (handlerUrl, action) {
        return handlerUrl + '::' + action;
    }

    // COMET settled a mutation carrying the handler's addInfoMessage() confirmation, which the
    // callbacks' cometMessages.showAll(cometRequest) then rendered. Adapters pass the same strings
    // as opts.info; a function form receives the mutation response so a message can quote a
    // server-computed value (e.g. the credited total).
    function infoFrom (info, response) {
        if (typeof info === 'function') {
            return info(response) || [];
        }
        return info || [];
    }

    // TTI-24321: classic frames and saves (requote, job details) read the COMET session order, which a REST
    // write leaves stale; reload it (REFRESH_ORDER_IN_SESSION_ACTION has no adapter) so they don't fail out of sync.
    function reloadSessionOrder () {
        if (typeof comet !== 'undefined' && typeof chandler !== 'undefined') {
            comet.newRequest(chandler, 'REFRESH_ORDER_IN_SESSION_ACTION', function () {}).send();
        }
    }

    // Every settle needs these, failures included: COMET callbacks read getWorkingObject() before checking
    // errors, and COMET's own getObject throws in REST mode (no xmlResponse), killing the callback.
    function installResultObjects (cometRequest, resultObjects) {
        var objects = resultObjects || {};
        cometRequest.getObject = function (name) {
            var k = name == null ? 'WORKINGOBJECT' : name;
            return Object.prototype.hasOwnProperty.call(objects, k) ? objects[k] : null;
        };
        cometRequest.getWorkingObject = function () {
            return cometRequest.getObject('WORKINGOBJECT');
        };
    }

    // Non-order screens (the product detail pages) register their working object; see setUnchangedWorkingObject.
    var unchangedWorkingObject = null;

    // A failed settle leaves the screen on the object it already has.
    function unchangedObjects (resultObjects) {
        if (resultObjects) {
            return resultObjects;
        }
        var corder = restComet.corder();
        if (corder) {
            return { WORKINGOBJECT: corder };
        }
        var current = unchangedWorkingObject ? unchangedWorkingObject() : null;
        return current ? { WORKINGOBJECT: current } : {};
    }

    return {
        enabled: function () {
            return typeof restUiEnabled !== 'undefined' && restUiEnabled === true;
        },

        // 87731: a screen with no pageglobals.corder registers how to read its own working object, so a
        // refused save re-renders it unchanged instead of handing the callback null.
        setUnchangedWorkingObject: function (fn) {
            unchangedWorkingObject = fn;
        },

        register: function (handlerUrl, action, adapterFn) {
            adapters[key(handlerUrl, action)] = adapterFn;
        },

        adapterFor: function (handlerUrl, action) {
            if (!this.enabled()) {
                return null;
            }
            var adapter = adapters[key(handlerUrl, action)];
            return adapter || null;
        },

        dispatch: function (cometRequest, adapter) {
            try {
                adapter(cometRequest);
            } catch (e) {
                logDebug('restComet adapter failed for ' + cometRequest.serverAction + ': ' + e);
                restComet.fail(cometRequest, ['An unexpected error occurred. Please try again.']);
            }
            return null;
        },

        // REST_ROOT_URL carries the caller's realm prefix (/rest/internal/v1/supplierAdmin
        // or /siteAdmin), so adapters pass realm-relative paths like '/orderItems/v2/1/lock'.
        //
        // A number of PATCH endpoints declare consumes=application/merge-patch+json
        // (quantityControls/inventoryControls/variantAttributes/displayProperties/pricingInfo/
        // associatedCategories, devices, pressSheetTypes, ...) and 415 a plain application/json
        // PATCH. The wire body is identical either way, so on a 415 re-send ONCE with the
        // merge-patch media type — a 415 means the server processed nothing, making the retry
        // safe, and it covers every current and future merge-patch call site without tracking
        // content types per adapter.
        ajax: function (method, path, body) {
            function send (contentType) {
                return $.ajax({
                    url: REST_ROOT_URL + path,
                    type: method,
                    contentType: contentType,
                    data: body == null ? null : JSON.stringify(body),
                    dataType: 'json'
                });
            }
            if (String(method).toUpperCase() !== 'PATCH') {
                return send('application/json');
            }
            var deferred = $.Deferred();
            send('application/json')
                .done(function () {
                    deferred.resolveWith(this, arguments);
                })
                .fail(function (jqXHR) {
                    if (jqXHR && jqXHR.status === 415) {
                        send('application/merge-patch+json')
                            .done(function () {
                                deferred.resolveWith(this, arguments);
                            })
                            .fail(function () {
                                deferred.rejectWith(this, arguments);
                            });
                        return;
                    }
                    deferred.rejectWith(this, arguments);
                });
            return deferred.promise();
        },

        // Settle successfully: install the result objects behind getObject()/getWorkingObject()
        // (keys match the COMET result names, e.g. WORKINGOBJECT, PAYMENTINFO) and run the
        // original success callback.
        succeed: function (cometRequest, resultObjects, messages) {
            cometRequest.commSuccess = true;
            cometRequest.serverSuccess = true;
            cometRequest.infoMessages = (messages && messages.info) || [];
            cometRequest.warningMessages = (messages && messages.warnings) || [];
            cometRequest.errorMessages = (messages && messages.errors) || [];
            installResultObjects(cometRequest, resultObjects);

            // Two callbacks (productionRuns.productionRunAdded, controller.saveLedgerAccounts) branch on
            // hasAnyErrorMessages() rather than hasErrorMessages(); alias so both names resolve.
            if (typeof cometRequest.hasErrorMessages === 'function') {
                cometRequest.hasAnyErrorMessages = cometRequest.hasErrorMessages;
            }
            return cometRequest.successResultHandlerFunction(cometRequest, cometRequest.successResultHandlerFunctionParameter);
        },

        // COMET delivered validation errors through the SUCCESS callback with error messages
        // populated (callers branch on hasErrorMessages()). Adapters map a 4xx WrapperDTO
        // response here so that contract holds in REST mode. resultObjects defaults to the current corder
        // as the working object, so a callback that re-renders from it shows the unchanged order.
        failValidation: function (cometRequest, errorMessages, resultObjects) {
            cometRequest.commSuccess = true;
            cometRequest.serverSuccess = true;
            cometRequest.infoMessages = [];
            cometRequest.warningMessages = [];
            cometRequest.errorMessages = errorMessages || [];
            installResultObjects(cometRequest, unchangedObjects(resultObjects));
            if (typeof cometRequest.hasErrorMessages === 'function') {
                cometRequest.hasAnyErrorMessages = cometRequest.hasErrorMessages;
            }
            return cometRequest.successResultHandlerFunction(cometRequest, cometRequest.successResultHandlerFunctionParameter);
        },

        // Hard failure (network/5xx): run the failure callback, or show the error in growl when there is none.
        fail: function (cometRequest, errorMessages, resultObjects) {
            cometRequest.commSuccess = false;
            cometRequest.errorMessages = errorMessages || [];
            installResultObjects(cometRequest, unchangedObjects(resultObjects));
            if (cometRequest.failureResultHandlerFunction) {
                return cometRequest.failureResultHandlerFunction(cometRequest, cometRequest.failureResultHandlerFunctionParameter);
            }
            if (typeof growlMessages !== 'undefined' && growlMessages && typeof growlMessages.showErrorMessage === 'function') {
                growlMessages.showErrorMessage(cometRequest.errorMessages.length
                    ? cometRequest.errorMessages.join(' ') : 'An unexpected error occurred. Please try again.');
            }
            return null;
        },

        // Stamp the classic CometBusinessObject instance accessors (cometObject.js:
        // get/getHtml/put/setProperty) onto a plain REST JSON node, so untouched COMET
        // callbacks that call them keep working in REST mode. The canonical consumer is the
        // shared countryStateZipController.js widget, whose updateStateList() callback calls
        // ccountry.setProperty('states', 'SerializedObject', ...) on each country handed to
        // pageglobals.ccountries — any adapter building that list must run its countries
        // through here. (Same shim as siteDetailController.js's siteDetailCometLike; the
        // COMET valueType and _properties bookkeeping are deliberately dropped — REST-mode
        // consumers only read the plain field back.)
        cometLike: function (obj) {
            obj = obj || {};
            obj.get = function (name) {
                return this[name];
            };
            obj.getHtml = function (name) {
                return strUnweb(this[name]);
            };
            obj.put = function (name, value) {
                this[name] = value;
            };
            obj.setProperty = function (name, valueType, value) {
                this[name] = value;
            };
            return obj;
        },

        // Read an addObject()-staged parameter as a plain object. comet.js stores BOTH the
        // JSON wire string (for the COMET channel) and, since the params-map fix, the raw
        // object (for adapters) under the same key — adapters written against the older
        // string shape called JSON.parse(params.<key>), which THROWS on the raw object and
        // dies silently inside restComet.dispatch's try/catch. Tolerate both shapes here.
        objectParam: function (cometRequest, key) {
            var value = cometRequest.params[key];
            if (value == null || value === '') {
                return null;
            }
            return typeof value === 'string' ? JSON.parse(value) : value;
        },

        // User-facing error strings from a failed V2 WrapperDTO ajax response.
        errorsFromResponse: function (jqXHR) {
            var wrapper = jqXHR && jqXHR.responseJSON;
            if (wrapper && wrapper.errors && wrapper.errors.length) {
                var out = [];
                for (var i = 0; i < wrapper.errors.length; i++) {
                    out.push(wrapper.errors[i].message || wrapper.errors[i].detail || 'Request failed');
                }
                return out;
            }
            // A 403 without a WrapperDTO body is the endpoint's securable-entity check refusing the user.
            if (jqXHR && jqXHR.status === 403) {
                return ['You do not have permission to do this. Ask an administrator to check your roles.'];
            }
            return ['Could not communicate with server. Please try again later.'];
        },

        // ---- Task C2: shared refresh-after-mutation helpers (built on restCorder, Task C1) ----------

        // pageglobals.corder — the order the screen is bound to, and its id.
        corder: function () {
            return typeof pageglobals !== 'undefined' && pageglobals.corder ? pageglobals.corder : null;
        },

        orderId: function () {
            var corder = restComet.corder();
            return corder ? corder.id : null;
        },

        // Resolve a POSITIONAL shipment index (how the shipping controller stages addObjectId) to the
        // real shipment id. Almost every C18 shipping adapter needs this before building its REST path.
        shipmentIdAt: function (index) {
            var shipments = typeof pageglobals !== 'undefined' && pageglobals.corder ? pageglobals.corder.shipments : null;
            return shipments && shipments[index] ? shipments[index].id : null;
        },

        // The repeated C3–C18 move: fire the mutating REST call, then re-fetch detail/init, rebuild the
        // corder (restCorder.refresh), and settle the SAME cometRequest with {WORKINGOBJECT: corder}.
        //   opts.extraObjects(corder) -> extra getObject() result keys (e.g. {ACTIVITYLOG:..., PAYMENTINFO:...})
        //   opts.patch(corder)        -> re-apply this action's known field change onto the rebuilt corder,
        //                                for fields the detail/init payload does not carry (see restCorder
        //                                rebuild-gap finding; e.g. LOCK_JOB_ACTION's item.locked).
        //   opts.info                 -> the COMET handler's success confirmation, as an array of strings or
        //                                a function(response) returning one. Without it the settle carries no
        //                                infoMessages and cometMessages.showAll renders nothing (TTI-21086).
        // COMET contract preserved: validation (4xx) surfaces through the SUCCESS callback with
        // errorMessages populated (failValidation); only comm/5xx failures run the failure callback (fail).
        mutateThenRefresh: function (cometRequest, ajaxPromise, opts) {
            var options = opts || {};

            function resultFor (corder) {
                var result = { WORKINGOBJECT: corder };
                if (typeof options.extraObjects === 'function') {
                    var extra = options.extraObjects(corder) || {};
                    for (var k in extra) {
                        if (Object.prototype.hasOwnProperty.call(extra, k)) {
                            result[k] = extra[k];
                        }
                    }
                }
                return result;
            }

            function settle (corder, response) {
                restComet.succeed(cometRequest, resultFor(corder), { info: infoFrom(options.info, response) });
            }

            ajaxPromise
                .done(function (response) {
                    reloadSessionOrder();
                    restCorder.refresh(restComet.orderId())
                        .done(function (corder) {
                            if (typeof options.patch === 'function') {
                                options.patch(corder);
                            }
                            settle(corder, response);
                        })
                        // The mutation already committed -- only the re-fetch failed. Running the failure
                        // callback here reports the write itself as failed and invites a retry, which on the
                        // CC-charge adapters charges the card a second time (TSP-707). Settle as the success
                        // it was, carrying the pre-refresh corder so the callbacks still have an order to
                        // render. options.patch is deliberately not applied: it re-applies the action's field
                        // change onto a REBUILT corder, and this one was never rebuilt.
                        //
                        // The stale notice goes straight to the growl queue rather than through the settle's
                        // warnings array, because succeed()'s warnings only reach the screen if the action's
                        // own success callback calls cometMessages.showAll -- and 21 of the 37 actions that
                        // route through here never do, which would leave a silent success showing stale values.
                        .fail(function () {
                            settle(restComet.corder(), response);
                            if (typeof growlMessages !== 'undefined') {
                                growlMessages.showWarningMessage(STALE_AFTER_MUTATION);
                            }
                        });
                })
                .fail(function (jqXHR) {
                    var status = jqXHR ? jqXHR.status : 0;
                    // Nothing changed server-side, so the callbacks get the order (and extra objects,
                    // e.g. ACTIVITYLOG) the screen already shows.
                    var unchanged = resultFor(restComet.corder());
                    if (status >= 400 && status < 500) {
                        restComet.failValidation(cometRequest, restComet.errorsFromResponse(jqXHR), unchanged);
                    } else {
                        restComet.fail(cometRequest, restComet.errorsFromResponse(jqXHR), unchanged);
                    }
                });
        }
    };
}());
